import { useCallback } from 'react';

import {
  VIDEO_PLAYBACK_PROCESSING,
  type VideoPlaybackAuthorization,
  type VideoPlaybackResolver,
} from '@shared/dream-components';

import { useEnsureCsrfToken } from '~/context/CsrfContext';

// Browser-side playback resolver for a rendered post. It posts the post id to the
// server action, which authorizes and returns a media URL for Orchid's own proxy
// route. Returns undefined without a post id so callers render the neutral
// placeholder instead of the player.
export const useCommunityVideoPlayback = (postId: string | undefined): VideoPlaybackResolver | undefined => {
  const ensureCsrfToken = useEnsureCsrfToken();

  const resolve = useCallback<VideoPlaybackResolver>(async (): ReturnType<VideoPlaybackResolver> => {
    if (!postId) return null;
    try {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch('/community_video_playback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        body: JSON.stringify({ postId }),
      });
      if (response.status === 409) return VIDEO_PLAYBACK_PROCESSING;
      if (!response.ok) return null;
      return (await response.json()) as VideoPlaybackAuthorization;
    } catch {
      return null;
    }
  }, [postId, ensureCsrfToken]);

  return postId ? resolve : undefined;
};

// Composer author preview: resolves playback for a video the current member owns
// by its id, before it is attached to a post. The player passes the video id, and
// the same server action authorizes it against the author's own uploads.
export const useCommunityVideoPreviewPlayback = (): VideoPlaybackResolver => {
  const ensureCsrfToken = useEnsureCsrfToken();

  return useCallback<VideoPlaybackResolver>(
    async (videoId): ReturnType<VideoPlaybackResolver> => {
      if (!videoId) return null;
      try {
        const csrfToken = await ensureCsrfToken();
        const response = await fetch('/community_video_playback', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          body: JSON.stringify({ videoId }),
        });
        if (response.status === 409) return VIDEO_PLAYBACK_PROCESSING;
        if (!response.ok) return null;
        return (await response.json()) as VideoPlaybackAuthorization;
      } catch {
        return null;
      }
    },
    [ensureCsrfToken],
  );
};
